export async function buildRootNode(url: string): Promise<Node> {
  const baseUrl = url.endsWith('/') ? url : url + '/';
  const treeUrl = new URL('tree', baseUrl).toString();
  // const response = await fetch(treeUrl, {
  //   method: 'GET', headers: new Headers({
  //     "ngrok-skip-browser-warning": "69420",
  //   }),
  // });
  const response = await fetch(treeUrl, {
    method: 'GET',
    headers: new Headers({
      "Authorization": `Bearer ${sessionStorage.getItem('userAccessToken') || ''}`,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch data from URL: ${treeUrl}, Status: ${response.status}`);
  }
  const contentType = response.headers.get('Content-Type');
  if (!contentType || !contentType.includes('application/json')) {
    console.log(await response.text());
    throw new Error(`Invalid content type: ${contentType}`);
  }

  const treeData = await response.json();
  const rootNode = new Node(`/${treeData.root_name}`, true, [], null);

  for (const fileName of treeData.files) {
    rootNode.insert(`/${treeData.root_name}/${fileName}`, false, null);
  }
  return rootNode;
}

export class Node {
  path: string;
  name: string;
  isFolder: boolean;
  active: boolean = false;
  items: Node[];
  content?: string | null;
  parentNode?: Node;
  blob: Blob | null = null;

  url: string | null = null;

  constructor(
    path: string,
    isFolder: boolean,
    items: Node[] = [],
    content?: string | null,
    parentNode?: Node,
  ) {
    this.path = path;
    const name = path.split('/').filter(Boolean).pop();
    if (!name) {
      throw new Error('Invalid path');
    }
    this.name = name;
    this.isFolder = isFolder;
    this.items = items;
    this.content = content;
    this.parentNode = parentNode;
  }

  insert(newNodePath: string, isFolder: boolean, content?: string | null): Node {
    if (newNodePath.startsWith(".")) {
      newNodePath = newNodePath.slice(1);
      const rootName = this.path.split('/')[1];
      newNodePath = `/${rootName}${newNodePath}`;
    }
    if (!newNodePath.startsWith(this.path)) {
      throw new Error(
        `New node path does not start with the current node path\nNew node path: ${newNodePath}\nCurrent node path: ${this.path}`, { cause : 'INVALID_NODE_PATH' }
      );
    }
    if (this.path === newNodePath) {
      this.isFolder = isFolder;
      this.content = content;
      return this;
    }
    const remainingPath = newNodePath.slice(this.path.length);
    const pathSegments = remainingPath.split('/').filter(Boolean);
    let nextNode = this.items.find((item) => item.name === pathSegments[0]);
    if (!nextNode) {
      nextNode = new Node(
        `${this.path}/${pathSegments[0]}`,
        true,
        [],
        undefined,
        this
      );
      this.items.push(nextNode);
    }
    const insertedNode = nextNode.insert(newNodePath, isFolder, content);
    this.sort();
    return insertedNode;
  }

  delete(targetNodePath: string) {
    if (this.path === targetNodePath) {
      if (!this.parentNode) {
        console.error('Cannot delete root node');
        return;
      }
      this.parentNode.items = this.parentNode.items.filter(
        (item) => item.name !== this.name
      );
    } else {
      const remainingPath = targetNodePath.slice(this.path.length);
      const pathSegments = remainingPath.split('/').filter(Boolean);
      const nextNode = this.items.find((item) => item.name === pathSegments[0]);
      if (!nextNode) {
        throw new Error(`Node not found ${targetNodePath}`);
      }
      nextNode.delete(targetNodePath);
    }
  }

  update(
    targetNodePath: string,
    newName: string,
    isFolder: boolean,
    content?: string
  ) {
    if (targetNodePath.startsWith(".")) {
      targetNodePath = targetNodePath.slice(1);
      const rootName = this.path.split('/')[1];
      targetNodePath = `/${rootName}${targetNodePath}`;
    }
    if (!targetNodePath.startsWith(this.path)) {
      throw new Error(
        `Target node path does not start with the current node path\nTarget node path: ${targetNodePath}\nCurrent node path: ${this.path}`
      );
    }
    if (this.path === targetNodePath) {
      this.isFolder = isFolder;
      this.content = content || '';
      this.name = newName;
      this.path = this.path.replace(this.name, newName);
      return;
    }
    const remainingPath = targetNodePath.slice(this.path.length);
    const pathSegments = remainingPath.split('/').filter(Boolean);
    const nextNode = this.items.find((item) => item.name === pathSegments[0]);
    if (!nextNode) {
      throw new Error(`Node not found ${targetNodePath}`);
    }
    nextNode.update(targetNodePath, newName, isFolder, content);
  }

  private sort(recursive: boolean = false): void {
    if (this.items && this.items.length > 0) {
      this.items.sort((a, b) => {
        if (a.isFolder === b.isFolder) {
          return a.name.localeCompare(b.name);
        }
        return a.isFolder ? -1 : 1;
      });
      if (recursive) {
        this.items
          .filter((item) => item.isFolder)
          .forEach((item) => item.sort());
      }
    }
  }

  async getBlob(forceFetch: boolean = false): Promise<Blob | null | undefined> {
    if (this.isFolder) {
      return undefined;
    }
    if (this.blob && !forceFetch) {
      return this.blob;
    } else {

      if (!this.url) {
        console.error('Server URL is not set. This should not happen. #235');
        const storedRepo = sessionStorage.getItem('repo');
        const repo = storedRepo ? JSON.parse(storedRepo) : null;
        if (!repo || !repo.url) {
          throw new Error('URL is not set. Repository information is missing in session storage.');
        } else {
          this.url = repo.url;
        }
      }
      const relativeFilePath = this.path.split('/').filter(Boolean).slice(1).join('/');
      const baseUrl = this.url!.endsWith('/') ? this.url : this.url + '/';
      const blobUrl = new URL(`read-file?file_path=${relativeFilePath}`, baseUrl!).toString();
      const response = await fetch(blobUrl, {
        method: 'GET',
        headers: new Headers({
          "Authorization": `Bearer ${sessionStorage.getItem('userAccessToken') || ''}`,
        }),
      });
      if (!response.ok) {
        console.error(response);
        throw new Error(`Failed to fetch blob for path: ${this.path}`);
      }
      const blob = await response.blob();
      this.blob = blob;
      return blob;
    }
  }

  async getContent(forceFetch: boolean = false): Promise<string | undefined | null> {
    if (this.isFolder) {
      return undefined;
    }
    if (this.content !== undefined && this.content !== null && !forceFetch) {
      return this.content;
    } else {
      const blob = await this.getBlob(forceFetch);
      this.content = blob ? await blob.text() : null;
      return this.content;
    }
  }

  getNode(path: string): Node | null {
    if (path.startsWith(".")) {
      path = path.slice(1);
      const rootName = this.path.split('/')[1];
      path = `/${rootName}${path}`;
    }
    if (this.path === path) {
      return this;
    }
    const remainingPath = path.slice(this.path.length);
    const pathSegments = remainingPath.split('/').filter(Boolean);
    const nextNode = this.items.find((item) => item.name === pathSegments[0]);
    if (!nextNode) {
      return null;
    }
    return nextNode.getNode(path);
  }

  markActiveInHierarchy() {
    this.active = true;
    if (this.parentNode) {
      this.parentNode.markActiveInHierarchy();
    }
  }
}
